import { useMemo, useState } from 'react';
import {
  BarChart3,
  Check,
  CircleAlert,
  CircleHelp,
  FileText,
  Info,
  List,
  Plus,
  UserRound,
  X,
} from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReToolTip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useEvaluateCourseSkillsMutation,
  useGetCourseSkillEvaluationListingQuery,
  useGetStudentCourseEvaluationReportByAssignmentQuery,
  useGetStudentCourseEvaluationReportMentorQuery,
  useGetStudentCourseEvaluationReportPeerQuery,
  useGetStudentCourseEvaluationReportSelfQuery,
} from '@/state/course';
import { toast } from 'sonner';

type SkillAssessmentUser = {
  id: string;
  name: string;
  email: string;
  photoUrl?: string | null;
};

type SkillAssessmentSkill = {
  id: string;
  name: string;
};

type SkillAssessmentDraft = {
  level: number;
  feedback: string;
};

interface CourseAssignmentSkillsAssessmentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  evaluationUsers: SkillAssessmentUser[];
  listingUsers: SkillAssessmentUser[];
  skills: SkillAssessmentSkill[];
  evaluationAssignmentId: number | null;
  evaluationCourseId: number;
  courseSectionId?: number | null;
  tenant: string;
  currentUserId: string | null;
}

const DREYFUS_LEVELS = [
  {
    level: 0,
    label: 'Not accessed',
    knowledge: '',
    standard: '',
    autonomy: '',
    complexity: '',
    context: '',
  },
  {
    level: 1,
    label: 'Novice',
    knowledge:
      "Minimal, or 'textbook' knowledge without connecting it to practice",
    standard: 'Unlikely to be satisfactory unless closely supervised',
    autonomy: 'Needs close supervision or instruction',
    complexity: 'Little or no conception of dealing with complexity',
    context: 'Tends to see actions in isolation',
  },
  {
    level: 2,
    label: 'Beginner',
    knowledge: 'Working knowledge of key aspects of practice',
    standard:
      'Straightforward tasks likely to be completed to an acceptable standard',
    autonomy:
      'Able to achieve some steps using own judgement, but supervision needed for overall task',
    complexity:
      'Appreciates complex situations but only able to achieve partial resolution',
    context: 'Sees actions as a series of steps',
  },
  {
    level: 3,
    label: 'Competent',
    knowledge: 'Good working and background knowledge of area of practice',
    standard: 'Fit for purpose, though may lack refinement',
    autonomy: 'Able to achieve most tasks using own judgement',
    complexity:
      'Copes with complex situations through deliberate analysis and planning',
    context: 'Sees actions at least partly in terms of longer-term goals',
  },
  {
    level: 4,
    label: 'Proficient',
    knowledge: 'Depth of understanding of discipline and area of practice',
    standard: 'Fully acceptable standard achieved routinely',
    autonomy:
      'Able to take full responsibility for own work (and that of others where applicable)',
    complexity:
      'Deals with complex situations holistically, decision-making more confident',
    context: "Sees overall 'picture' and how individual actions fit within it",
  },
  {
    level: 5,
    label: 'Expert',
    knowledge:
      'Authoritative knowledge of discipline and deep tacit understanding across area of practice',
    standard: 'Excellence achieved with relative ease',
    autonomy:
      'Able to take responsibility for going beyond existing standards and creating own interpretations',
    complexity:
      'Holistic grasp of complex situations, moves between intuitive and analytical approaches with ease',
    context:
      "Sees overall 'picture' and alternative approaches; vision of what may be possible",
  },
];
const buildLevelTooltip = (level: (typeof DREYFUS_LEVELS)[number]) => (
  <div className="space-y-2 text-xs">
    <p className="font-semibold">
      {level.level}. {level.label}
    </p>
    <div className="space-y-1 text-muted-foreground">
      <strong>Knowledge</strong>
      <p>{level.knowledge}</p>
      <strong>Standard of work</strong>
      <p>{level.standard}</p>
      <strong>Autonomy</strong>
      <p>{level.autonomy}</p>
      <strong>Coping with complexity</strong>
      <p>{level.complexity}</p>
      <strong>Perception of context</strong>
      <p>{level.context}</p>
    </div>
  </div>
);

const CourseAssignmentSkillsAssessmentSheet = ({
  open,
  onOpenChange,
  evaluationUsers,
  listingUsers,
  skills,
  evaluationAssignmentId,
  evaluationCourseId,
  courseSectionId = null,
  tenant,
  currentUserId,
}: CourseAssignmentSkillsAssessmentSheetProps) => {
  const [isEvaluationOpen, setIsEvaluationOpen] = useState(false);
  const [selectedListingUserId, setSelectedListingUserId] = useState<
    string | null
  >(null);
  const [selectedEvaluationUserId, setSelectedEvaluationUserId] = useState<
    string | null
  >(null);
  const [draftsByUserId, setDraftsByUserId] = useState<
    Record<string, Record<string, SkillAssessmentDraft>>
  >({});
  const [showSubmitWarning, setShowSubmitWarning] = useState(false);
  const [viewMode, setViewMode] = useState<'chart' | 'list'>('chart');
  const [evaluateCourseSkills, { isLoading: isSubmitting }] =
    useEvaluateCourseSkillsMutation();

  const effectiveListingUserId =
    selectedListingUserId &&
    listingUsers.some((user) => user.id === selectedListingUserId)
      ? selectedListingUserId
      : (listingUsers[0]?.id ?? null);
  const effectiveEvaluationUserId =
    selectedEvaluationUserId &&
    evaluationUsers.some((user) => user.id === selectedEvaluationUserId)
      ? selectedEvaluationUserId
      : (evaluationUsers[0]?.id ?? null);

  const selectedListingUser = useMemo(
    () =>
      listingUsers.find((user) => user.id === effectiveListingUserId) ?? null,
    [listingUsers, effectiveListingUserId]
  );
  const selectedEvaluationUser = useMemo(
    () =>
      evaluationUsers.find((user) => user.id === effectiveEvaluationUserId) ??
      null,
    [evaluationUsers, effectiveEvaluationUserId]
  );

  const {
    data: courseSkillEvaluationListing = [],
    isLoading: isListingLoading,
    refetch: refetchListing,
  } = useGetCourseSkillEvaluationListingQuery(
    open && effectiveListingUserId && evaluationAssignmentId
      ? {
          tenant,
          courseId: evaluationCourseId,
          assignmentId: evaluationAssignmentId,
          userId: effectiveListingUserId,
        }
      : skipToken
  );

  const overallReportQueryArg =
    open && effectiveListingUserId && evaluationAssignmentId
      ? {
          tenant,
          courseId: evaluationCourseId,
          assignmentId: evaluationAssignmentId,
          studentId: effectiveListingUserId,
        }
      : skipToken;

  const { data: overallReport = [], refetch: refetchOverall } =
    useGetStudentCourseEvaluationReportByAssignmentQuery(overallReportQueryArg);
  const { data: selfReport = [], refetch: refetchSelf } =
    useGetStudentCourseEvaluationReportSelfQuery(overallReportQueryArg);
  const { data: peerReport = [], refetch: refetchPeer } =
    useGetStudentCourseEvaluationReportPeerQuery(overallReportQueryArg);
  const { data: mentorReport = [], refetch: refetchMentor } =
    useGetStudentCourseEvaluationReportMentorQuery(overallReportQueryArg);

  const chartData = useMemo(() => {
    const map = new Map<
      string,
      {
        skill: string;
        overall: number;
        self: number;
        peer: number;
        mentor: number;
      }
    >();
    const ensureRow = (name: string) => {
      if (!map.has(name)) {
        map.set(name, { skill: name, overall: 0, self: 0, peer: 0, mentor: 0 });
      }
      return map.get(name)!;
    };
    overallReport.forEach((item) => {
      const row = ensureRow(item.skill?.name ?? 'Skill');
      row.overall = Number(item.total_evaluated_points ?? 0);
    });
    selfReport.forEach((item) => {
      const row = ensureRow(item.skill?.name ?? 'Skill');
      row.self = Number(item.total_evaluated_points ?? 0);
    });
    peerReport.forEach((item) => {
      const row = ensureRow(item.skill?.name ?? 'Skill');
      row.peer = Number(item.total_evaluated_points ?? 0);
    });
    mentorReport.forEach((item) => {
      const row = ensureRow(item.skill?.name ?? 'Skill');
      row.mentor = Number(item.total_evaluated_points ?? 0);
    });
    return Array.from(map.values());
  }, [overallReport, selfReport, peerReport, mentorReport]);
  const hasCurrentUserEvaluation = useMemo(() => {
    if (!currentUserId) return false;
    return courseSkillEvaluationListing.some(
      (item) => item.evaluator_id === currentUserId
    );
  }, [courseSkillEvaluationListing, currentUserId]);

  const getDraft = (userId: string, skillId: string): SkillAssessmentDraft => {
    return draftsByUserId[userId]?.[skillId] ?? { level: 0, feedback: '' };
  };

  const updateDraft = (
    userId: string,
    skillId: string,
    next: Partial<SkillAssessmentDraft>
  ) => {
    setDraftsByUserId((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] ?? {}),
        [skillId]: {
          ...getDraft(userId, skillId),
          ...next,
        },
      },
    }));
  };

  const userProgressById = useMemo(() => {
    const map = new Map<string, { evaluated: number; total: number }>();
    evaluationUsers.forEach((user) => {
      const drafts = draftsByUserId[user.id] ?? {};
      let evaluated = 0;
      skills.forEach((skill) => {
        const draft = drafts[skill.id];
        if (draft && draft.level > 0) evaluated += 1;
      });
      map.set(user.id, { evaluated, total: skills.length });
    });
    return map;
  }, [draftsByUserId, skills, evaluationUsers]);

  const hasIncompleteEvaluations = useMemo(() => {
    return evaluationUsers.some((user) => {
      const progress = userProgressById.get(user.id);
      if (!progress) return false;
      return progress.evaluated > 0 && progress.evaluated < progress.total;
    });
  }, [userProgressById, evaluationUsers]);

  const hasAnyEvaluationStarted = useMemo(() => {
    return evaluationUsers.some((user) => {
      const progress = userProgressById.get(user.id);
      return progress ? progress.evaluated > 0 : false;
    });
  }, [userProgressById, evaluationUsers]);

  const buildPayload = () => {
    if (!evaluationAssignmentId) return [];
    const rows: Array<{
      evaluation_points: number;
      user_id: string;
      user_skills: number;
      feedback: string;
      evaluation_assignment: number;
      evaluation_course: number;
      course_section: number | null;
    }> = [];
    evaluationUsers.forEach((user) => {
      const drafts = draftsByUserId[user.id] ?? {};
      skills.forEach((skill) => {
        const draft = drafts[skill.id];
        if (!draft || draft.level <= 0) return;
        rows.push({
          evaluation_points: draft.level,
          user_id: user.id,
          user_skills: Number(skill.id),
          feedback: draft.feedback || '',
          evaluation_assignment: evaluationAssignmentId,
          evaluation_course: evaluationCourseId,
          course_section: courseSectionId ?? null,
        });
      });
    });
    return rows;
  };

  const handleSubmit = async () => {
    if (!evaluationAssignmentId) {
      toast.error('Missing assignment context.');
      return;
    }
    const rows = buildPayload();
    if (!rows.length) {
      toast.error('Add at least one skill evaluation before submitting.');
      return;
    }
    try {
      await evaluateCourseSkills({
        tenant,
        data: { students: rows },
      }).unwrap();
      toast.success('Skill evaluation submitted.');
      refetchListing();
      refetchOverall();
      refetchSelf();
      refetchPeer();
      refetchMentor();
      setShowSubmitWarning(false);
      setIsEvaluationOpen(false);
    } catch {
      toast.error('Failed to submit skill evaluation.');
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full p-0 sm:max-w-[min(97vw,1200px)]"
          showClose={false}
        >
          <SheetHeader className="border-b px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
                <SheetTitle className="text-base font-semibold">
                  Skill assessment
                </SheetTitle>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <a
                  href="https://devmts.org.uk/dreyfus.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="hidden text-xs font-medium text-primary hover:underline sm:inline"
                >
                  © Stan Lester Developments 2005-2021. Dreyfus Model.
                </a>
                <a
                  href="https://devmts.org.uk/dreyfus.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted sm:hidden"
                >
                  <Info className="h-4 w-4" />
                  <span className="sr-only">Dreyfus Model</span>
                </a>
                {!isListingLoading &&
                courseSkillEvaluationListing.length > 0 &&
                !hasCurrentUserEvaluation ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3 sm:gap-2"
                    onClick={() => setIsEvaluationOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">
                      Add your evaluation
                    </span>
                  </Button>
                ) : null}
              </div>
            </div>
          </SheetHeader>
          <div className="grid min-h-[70vh] flex-1 grid-cols-1 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="border-b p-4 sm:max-h-[40vh] sm:overflow-y-auto lg:max-h-none lg:overflow-visible lg:border-b-0 lg:border-r">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Select users
              </p>
              <div className="mt-3 space-y-2">
                {listingUsers.map((user) => {
                  const isSelected = effectiveListingUserId === user.id;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                        isSelected
                          ? 'border-primary/40 bg-primary/10'
                          : 'border-muted/60 hover:border-primary/30'
                      }`}
                      onClick={() => setSelectedListingUserId(user.id)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.photoUrl || undefined} />
                        <AvatarFallback>
                          {user.name ? (
                            user.name.slice(0, 1)
                          ) : (
                            <UserRound className="h-4 w-4" />
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {user.email || user.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {user.name}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex min-h-0 flex-col lg:min-h-[70vh]">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <p className="text-sm font-semibold">
                    {selectedListingUser?.name || 'Skills overview'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedListingUser?.email || 'Select a user'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant={viewMode === 'chart' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="gap-2"
                    onClick={() => setViewMode('chart')}
                  >
                    <BarChart3 className="h-4 w-4" />
                    Chart
                  </Button>
                  <Button
                    type="button"
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="gap-2"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="h-4 w-4" />
                    List
                  </Button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {isListingLoading ? (
                  <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                    Loading skill evaluations...
                  </div>
                ) : courseSkillEvaluationListing.length === 0 ? (
                  <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 py-12 text-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted/40">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">No data</p>
                      <p className="text-xs text-muted-foreground">
                        Add skills evaluation to get started.
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setIsEvaluationOpen(true)}
                    >
                      <Plus className="h-4 w-4" />
                      Add skills evaluation
                    </Button>
                  </div>
                ) : viewMode === 'chart' ? (
                  <div className="min-h-[420px] rounded-xl border bg-background p-4">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <p className="text-sm font-semibold">Skills overview</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-sky-400" />
                          Overall skill GPA
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-emerald-400" />
                          Self evaluation
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-amber-400" />
                          Peer evaluation
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-orange-500" />
                          Instructor evaluation
                        </span>
                      </div>
                    </div>
                    <div className="h-[320px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartData}
                          margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
                          barSize={14}
                          barCategoryGap={18}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="skill" />
                          <YAxis />
                          <ReToolTip
                            cursor={{ fill: 'rgba(148, 163, 184, 0.12)' }}
                            contentStyle={{
                              borderRadius: '10px',
                              border: '1px solid rgba(148,163,184,0.3)',
                            }}
                          />
                          <Bar
                            dataKey="overall"
                            name="Overall skill GPA"
                            stackId="a"
                            fill="#38bdf8"
                          />
                          <Bar
                            dataKey="self"
                            name="Self evaluation"
                            stackId="a"
                            fill="#34d399"
                          />
                          <Bar
                            dataKey="peer"
                            name="Peer evaluation"
                            stackId="a"
                            fill="#fbbf24"
                          />
                          <Bar
                            dataKey="mentor"
                            name="Instructor evaluation"
                            stackId="a"
                            fill="#f97316"
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="min-h-[420px] rounded-xl border bg-background">
                    <div className="w-full overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
                          <tr>
                            <th className="px-3 py-2">Skill</th>
                            <th className="px-3 py-2 text-right">
                              Overall skill GPA
                            </th>
                            <th className="px-3 py-2 text-right">
                              Self evaluation
                            </th>
                            <th className="px-3 py-2 text-right">
                              Peer evaluation
                            </th>
                            <th className="px-3 py-2 text-right">
                              Instructor evaluation
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chartData.map((row, index) => (
                            <tr
                              key={row.skill}
                              className={index ? 'border-t' : ''}
                            >
                              <td className="px-3 py-2 font-medium">
                                {row.skill}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {row.overall.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {row.self.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {row.peer.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                {row.mentor.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={isEvaluationOpen} onOpenChange={setIsEvaluationOpen}>
        <SheetContent
          side="right"
          className="flex h-full w-full flex-col p-0 sm:max-w-[min(97vw,1280px)]"
          showClose={false}
        >
          <SheetHeader className="border-b px-4 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted"
                  onClick={() => setIsEvaluationOpen(false)}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
                <SheetTitle className="text-base font-semibold">
                  Skills evaluation
                </SheetTitle>
              </div>
              <a
                href="https://devmts.org.uk/dreyfus.pdf"
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-primary hover:underline"
              >
                © Stan Lester Developments 2005-2021. Dreyfus Model.
              </a>
            </div>
          </SheetHeader>

          <div className="grid min-h-[70vh] grid-cols-1 gap-0 lg:grid-cols-[280px_minmax(0,1fr)]">
            <div className="border-b p-4 lg:border-b-0 lg:border-r">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Select users
              </p>
              <div className="mt-3 space-y-2">
                {evaluationUsers.map((user) => {
                  const isSelected = effectiveEvaluationUserId === user.id;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                        isSelected
                          ? 'border-primary/40 bg-primary/10'
                          : 'border-muted/60 hover:border-primary/30'
                      }`}
                      onClick={() => setSelectedEvaluationUserId(user.id)}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={user.photoUrl || undefined} />
                        <AvatarFallback>
                          {user.name ? (
                            user.name.slice(0, 1)
                          ) : (
                            <UserRound className="h-4 w-4" />
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {user.email || user.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {user.name}
                        </p>
                      </div>
                      {(() => {
                        const progress = userProgressById.get(user.id);
                        if (
                          !progress ||
                          (!hasAnyEvaluationStarted && progress.evaluated === 0)
                        ) {
                          return null;
                        }
                        const isComplete =
                          progress.evaluated >= progress.total &&
                          progress.total > 0;
                        return isComplete ? (
                          <Check className="ml-auto h-4 w-4 text-emerald-600" />
                        ) : (
                          <CircleAlert className="ml-auto h-4 w-4 text-amber-600" />
                        );
                      })()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex min-h-0 flex-col">
              <div className="flex-1 overflow-y-auto p-4">
                {!selectedEvaluationUser ? (
                  <>
                    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                      <p className="font-semibold">Warning!</p>
                      <p className="mt-1 text-xs">
                        The evaluations you submit cannot be edited or deleted
                        once submitted. Please take your time and fill out the
                        form carefully.
                      </p>
                    </div>

                    <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
                      <p className="text-xs">
                        1. Select a user on the left and add your evaluations.
                      </p>
                      <p className="text-xs">
                        2. Repeat the same for all users one at a time. After
                        evaluations for all users are done, click submit to save
                        them.
                      </p>
                    </div>
                  </>
                ) : null}

                {!selectedEvaluationUser ? (
                  <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                    Select at least one user to start evaluating skills.
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        <AvatarImage
                          src={selectedEvaluationUser.photoUrl || undefined}
                        />
                        <AvatarFallback>
                          {selectedEvaluationUser.name ? (
                            selectedEvaluationUser.name.slice(0, 1)
                          ) : (
                            <UserRound className="h-4 w-4" />
                          )}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold">
                          Skills evaluation for{' '}
                          {selectedEvaluationUser.email ||
                            selectedEvaluationUser.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {selectedEvaluationUser.name}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {skills.map((skill) => {
                        const draft = getDraft(
                          selectedEvaluationUser.id,
                          skill.id
                        );
                        const levelMeta =
                          DREYFUS_LEVELS[draft.level] ?? DREYFUS_LEVELS[0];
                        return (
                          <div
                            key={`${selectedEvaluationUser.id}-${skill.id}`}
                            className="rounded-xl border bg-background p-4"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-primary">
                                  {skill.name}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{levelMeta.label}</span>
                                {draft.level > 0 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex h-5 w-5 items-center justify-center rounded-full border text-muted-foreground"
                                      >
                                        <CircleHelp className="h-3.5 w-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      className="max-w-xs"
                                      side="left"
                                    >
                                      {buildLevelTooltip(levelMeta)}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}
                              </div>
                            </div>

                            <div className="mt-4 space-y-2">
                              <div className="flex items-center justify-between text-xs text-muted-foreground">
                                <span>Evaluation</span>
                                <span>{draft.level}</span>
                              </div>
                              <Slider
                                value={[draft.level]}
                                min={0}
                                max={5}
                                step={1}
                                onValueChange={(value) => {
                                  const nextLevel = value[0] ?? 0;
                                  updateDraft(
                                    selectedEvaluationUser.id,
                                    skill.id,
                                    { level: nextLevel }
                                  );
                                }}
                              />
                            </div>

                            <div className="mt-4">
                              <Textarea
                                value={draft.feedback}
                                onChange={(event) =>
                                  updateDraft(
                                    selectedEvaluationUser.id,
                                    skill.id,
                                    { feedback: event.target.value }
                                  )
                                }
                                placeholder="Add feedback for this skills"
                                className="min-h-[90px]"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-auto border-t px-4 py-3">
            <div className="flex items-center justify-end">
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  if (hasIncompleteEvaluations) {
                    setShowSubmitWarning(true);
                  } else {
                    void handleSubmit();
                  }
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showSubmitWarning} onOpenChange={setShowSubmitWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit incomplete evaluations?</AlertDialogTitle>
            <AlertDialogDescription>
              Every user&apos;s skills have not been evaluated yet. You can
              still submit, but incomplete evaluations will be missing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleSubmit();
              }}
            >
              Submit anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CourseAssignmentSkillsAssessmentSheet;
